/** English UI strings. Keys are grouped by screen; `{name}` placeholders are filled by t(). */
export const en = {
  // --- Shared --------------------------------------------------------------
  "common.app_name": "Dolphin Booking",
  "common.language": "Language",
  "common.loading": "Loading",
  "common.save": "Save",
  "common.saved": "Saved.",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.remove": "Remove",
  "common.optional": "(optional)",
  "common.day": "Day",
  "common.today": "Today",
  "common.previous": "Previous",
  "common.next": "Next",
  "common.start": "Start",
  "common.end": "End",
  "common.minutes": "{n} min",
  "common.error_generic": "Something went wrong. Please try again.",
  "common.error_load": "Could not load this page. Refresh to try again.",
  "common.free": "Free",
  "common.yes": "Yes",
  "common.no": "No",

  // --- Navigation ------------------------------------------------------------
  "nav.main": "Main",
  "nav.overview": "Overview",
  "nav.bookings": "Bookings",
  "nav.practitioners": "Practitioners",
  "nav.services": "Services",
  "nav.settings": "Clinic settings",
  "nav.profile": "Profile",
  "nav.sign_out": "Sign out",
  "nav.sign_in": "Sign in",
  "nav.start_free": "Start free",
  "nav.open_dashboard": "Open dashboard",
  "nav.clinic": "Clinic",

  // --- Landing page ----------------------------------------------------------
  "landing.tagline": "Online booking for clinics",
  "landing.headline": "Let patients book online. Spend your time on treatments.",
  "landing.lede":
    "Dolphin Booking is the booking system for independent clinics: physiotherapists, chiropractors, massage therapists and more. Patients book free slots around the clock, and confirmations and reminders go out by themselves.",
  "landing.cta_setup": "Set up your clinic",
  "landing.cta_account": "I already have an account",
  "landing.trust_eu": "Hosted in the EU",
  "landing.trust_gdpr": "Built for GDPR",
  "landing.trust_lang": "Danish and English",
  "landing.hero_alt": "Dolphin Booking's dolphin leaping out of a wave",
  "landing.features_title": "Everything a busy clinic needs, nothing it doesn't",
  "landing.feature1_title": "Online booking 24/7",
  "landing.feature1_text":
    "Patients see the times that are really free and book in under a minute, without creating an account.",
  "landing.feature2_title": "No double bookings",
  "landing.feature2_text": "Availability is checked per practitioner and enforced by the database itself.",
  "landing.feature3_title": "Automatic reminders",
  "landing.feature3_text": "Confirmations, change notices and 24-hour reminders by email, and by SMS if you want it.",
  "landing.feature4_title": "Your calendar, synced",
  "landing.feature4_text": "Each practitioner gets a private calendar feed for Google, Apple or Outlook calendar.",
  "landing.feature5_title": "Built for teams",
  "landing.feature5_text": "Invite practitioners and administrators, and set working hours and services per person.",
  "landing.feature6_title": "Insights",
  "landing.feature6_text": "See bookings, cancellations, no-shows and your busiest hours at a glance.",
  "landing.steps_title": "Up and running in three steps",
  "landing.step1_title": "Create your clinic",
  "landing.step1_text": "Add your services, prices and practitioners with their working hours.",
  "landing.step2_title": "Share your booking page",
  "landing.step2_text": "Link to it from your website, Google profile or social media.",
  "landing.step3_title": "Patients book themselves",
  "landing.step3_text": "Bookings land in your calendar, and patients can move or cancel within your rules.",
  "landing.audience_title": "Made for independent clinics",
  "landing.audience_list":
    "Physiotherapists · Chiropractors · Massage therapists · Osteopaths · Psychologists · Reflexologists · Beauty clinics",
  "landing.privacy_title": "Privacy built in, not bolted on",
  "landing.privacy_text":
    "Your patients' data is isolated per clinic in the database, hosted in the EU, and never used for anything but your bookings. No ads, no tracking, no cookie banners. You get a data processing agreement and tools to export or erase a patient's data.",
  "landing.privacy_link": "Read the privacy policy",
  "landing.faq_title": "Frequently asked questions",
  "landing.faq1_q": "Do my patients need an account to book?",
  "landing.faq1_a": "No. Patients book with their name and email, and get a link to view, move or cancel the booking.",
  "landing.faq2_q": "Is Dolphin Booking GDPR compliant?",
  "landing.faq2_a":
    "Yes. Data is hosted in the EU, each clinic's data is isolated, old data is anonymised automatically, and every clinic gets a data processing agreement.",
  "landing.faq3_q": "Can patients get SMS reminders?",
  "landing.faq3_a":
    "Yes. Email reminders are always included, and you can turn on SMS reminders 24 hours before each appointment.",
  "landing.faq4_q": "Is the booking page available in Danish and English?",
  "landing.faq4_a": "Yes. Patients choose their language, and emails follow the language they booked in.",
  "landing.faq5_q": "Can I put the booking page on my own website?",
  "landing.faq5_a":
    "Yes. Every clinic gets its own booking address that you can link to from your website, Google profile or social media.",
  "landing.cta_title": "Ready to fill your calendar?",
  "landing.cta_text": "Set up your clinic in a few minutes.",

  // --- Auth ------------------------------------------------------------------
  "auth.accept_terms_before": "On behalf of the clinic, I accept the",
  "auth.accept_terms_and": "and the",
  "auth.accept_terms_after": ".",
  "auth.signup_title": "Set up your clinic",
  "auth.already_registered": "Already registered?",
  "auth.new_here": "New here?",
  "auth.clinic_name": "Clinic name",
  "auth.booking_address": "Booking page address",
  "auth.booking_address_hint": "Lowercase letters, numbers and hyphens",
  "auth.booking_address_preview": "Patients will book at {url}",
  "auth.your_name": "Your name",
  "auth.work_email": "Work email",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.password_hint": "At least {n} characters. A short sentence works well.",
  "auth.create_clinic": "Create clinic",
  "auth.sign_in": "Sign in",
  "auth.forgot_password": "Forgot password?",
  "auth.login_failed": "Incorrect email or password",
  "auth.reset_title": "Reset your password",
  "auth.reset_subtitle": "We'll email you a link to choose a new one.",
  "auth.reset_sent": "If an account exists for that email, a reset link is on its way. It expires in 30 minutes.",
  "auth.send_reset_link": "Send reset link",
  "auth.back_to_sign_in": "Back to sign in",
  "auth.new_password_title": "Choose a new password",
  "auth.link_incomplete": "This link is incomplete. Request a new reset email.",
  "auth.password_changed_signed_out": "Your password has been changed. All other sessions were signed out.",
  "auth.new_password": "New password",
  "auth.new_password_hint": "At least {n} characters.",
  "auth.repeat_new_password": "Repeat new password",
  "auth.passwords_mismatch": "The passwords do not match",
  "auth.change_password": "Change password",
  "auth.not_found_title": "Page not found",
  "auth.not_found_text": "The page you were looking for doesn't exist.",
  "auth.go_home": "Go to the home page",

  // --- Dashboard shell and overview -------------------------------------------
  "app.account_load_failed": "Could not load your account. Refresh the page to try again.",
  "app.not_linked": "Your account is not linked to a clinic yet.",
  "app.welcome": "Welcome, {name}",
  "app.role_owner": "Owner",
  "app.role_admin": "Admin",
  "app.role_practitioner": "Practitioner",
  "app.active_services": "Active services",
  "app.practitioners": "Practitioners",
  "app.bookings_today": "Bookings today",
  "app.get_started": "Get started",
  "app.get_started_services": "Add the services patients can book.",
  "app.add_first_service": "Add your first service →",
  "app.get_started_practitioners": "Add a practitioner with working hours so patients can book.",
  "app.add_first_practitioner": "Add a practitioner →",
  "app.booking_page": "Your booking page",
  "app.booking_page_text": "Share this link with patients. It is live as soon as a practitioner has working hours.",
  "app.open_booking_page": "Open booking page",

  // --- Services -----------------------------------------------------------------
  "services.title": "Services",
  "services.subtitle": "What patients can book, how long it takes and what it costs.",
  "services.show_inactive": "Show inactive",
  "services.load_failed": "Could not load services.",
  "services.empty": "No services yet.",
  "services.deactivate": "Deactivate",
  "services.activate": "Activate",
  "services.add_title": "Add a service",
  "services.name": "Name",
  "services.name_placeholder": "e.g. Initial consultation",
  "services.duration": "Duration (minutes)",
  "services.price": "Price (DKK)",
  "services.description": "Description (optional)",
  "services.add_button": "Add service",

  // --- Clinic settings --------------------------------------------------------------
  "settings.title": "Clinic settings",
  "settings.load_failed": "Could not load clinic settings.",
  "settings.saved": "Settings saved.",
  "settings.clinic_name": "Clinic name",
  "settings.timezone": "Time zone",
  "settings.timezone_hint": "IANA name, e.g. Europe/Copenhagen. Working hours and slots use this time zone.",
  "settings.booking_page": "Booking page",
  "settings.default_language": "Booking page language",
  "settings.default_language_hint": "Shown to patients until they pick a language themselves.",
  "settings.customer_accounts": "Let patients create accounts",
  "settings.customer_accounts_text":
    "Patients can always book as guests. With accounts on, they can also sign in to see and manage their bookings.",
  "settings.public_profile": "Public profile",
  "settings.booking_rules": "Booking rules",
  "settings.slot_interval": "Minutes between start times",
  "settings.min_notice": "Minimum notice for online bookings (minutes)",
  "settings.max_advance": "How far ahead patients can book (days)",
  "settings.cancellation_notice": "Patients can move or cancel online until (minutes before)",
  "settings.save": "Save settings",
  "lang.en": "English",
  "lang.da": "Danish",

  // --- Profile -----------------------------------------------------------------------
  "profile.title": "Profile",
  "profile.details": "Your details",
  "profile.name": "Name",
  "profile.email": "Email",
  "profile.change_password": "Change password",
  "profile.change_password_text": "You'll stay signed in here; other devices are signed out.",
  "profile.current_password": "Current password",
  "profile.new_password": "New password",
  "profile.password_changed": "Password changed.",

  // --- Practitioners -------------------------------------------------------------------
  "practitioners.title": "Practitioners",
  "practitioners.subtitle": "Who patients can book, which services they offer and when they work.",
  "practitioners.show_inactive": "Show inactive",
  "practitioners.empty": "No practitioners yet.",
  "practitioners.load_failed": "Could not load practitioners.",
  "practitioners.add_title": "Add a practitioner",
  "practitioners.name": "Name",
  "practitioners.job_title": "Title (optional)",
  "practitioners.job_title_placeholder": "e.g. Physiotherapist",
  "practitioners.services": "Services offered",
  "practitioners.no_services_hint": "Add services first, then link them here.",
  "practitioners.add_button": "Add practitioner",
  "practitioners.edit_title": "Edit practitioner",
  "practitioners.active": "Active (bookable online)",
  "practitioners.inactive": "inactive",
  "practitioners.no_hours": "No working hours yet",
  "practitioners.hours_summary": "{days} days a week",
  "practitioners.working_hours": "Working hours",
  "practitioners.working_hours_hint":
    "Times are in the clinic's time zone ({tz}). Add several blocks on a day to leave a break.",
  "practitioners.add_block": "Add hours",
  "practitioners.save_hours": "Save working hours",
  "practitioners.hours_saved": "Working hours saved.",
  "practitioners.time_off": "Time off",
  "practitioners.time_off_hint": "Holidays, courses or a day's absence. No times are offered during time off.",
  "practitioners.time_off_empty": "No time off in the next 90 days.",
  "practitioners.time_off_reason": "Reason (optional)",
  "practitioners.add_time_off": "Add time off",
  "practitioners.time_off_added": "Time off added.",
  "practitioners.saved": "Practitioner saved.",
  "weekday.1": "Monday",
  "weekday.2": "Tuesday",
  "weekday.3": "Wednesday",
  "weekday.4": "Thursday",
  "weekday.5": "Friday",
  "weekday.6": "Saturday",
  "weekday.7": "Sunday",

  // --- Bookings (staff) ------------------------------------------------------------------
  "bookings.title": "Bookings",
  "bookings.subtitle": "Appointments by day. Move, cancel or record attendance.",
  "bookings.practitioner": "Practitioner",
  "bookings.all_practitioners": "All practitioners",
  "bookings.status": "Status",
  "bookings.all_statuses": "All statuses",
  "bookings.status_confirmed": "Confirmed",
  "bookings.status_cancelled": "Cancelled",
  "bookings.status_completed": "Completed",
  "bookings.status_no_show": "No-show",
  "bookings.empty": "No bookings on this day.",
  "bookings.load_failed": "Could not load bookings.",
  "bookings.new_booking": "New booking",
  "bookings.new_hint":
    "Walk-in or phone booking. Online rules (notice and working hours) don't apply; overlaps are still prevented.",
  "bookings.service": "Service",
  "bookings.date": "Date",
  "bookings.time": "Time",
  "bookings.customer_name": "Patient name",
  "bookings.customer_email": "Patient email",
  "bookings.customer_phone": "Phone (optional)",
  "bookings.notes": "Notes (optional)",
  "bookings.language": "Patient's language (emails)",
  "bookings.send_confirmation": "Email a confirmation to the patient",
  "bookings.create_button": "Create booking",
  "bookings.created": "Booking created.",
  "bookings.cancel_booking": "Cancel booking",
  "bookings.cancel_confirm": "Cancel this booking? The patient will be emailed.",
  "bookings.cancel_reason": "Reason (optional)",
  "bookings.cancelled": "Booking cancelled.",
  "bookings.mark_completed": "Completed",
  "bookings.mark_no_show": "No-show",
  "bookings.customer_data": "Customer data",
  "bookings.customer_data_text":
    "Download everything stored about {name} (for an access or portability request), or erase their personal data. Erasing anonymises the customer, removes notes from all their bookings and cancels their future bookings without notifying them. It cannot be undone.",
  "bookings.customer_export": "Download data (JSON)",
  "bookings.customer_erase": "Erase customer",
  "bookings.customer_erase_confirm": "Erase all personal data about {name}? This cannot be undone.",
  "bookings.customer_erased": "The customer's personal data was erased.",
  "bookings.move": "Move",
  "bookings.move_title": "Move booking",
  "bookings.move_hint": "Pick a new date and time. The patient will be emailed.",
  "bookings.move_button": "Move booking",
  "bookings.moved": "Booking moved.",
  "bookings.updated": "Booking updated.",
  "bookings.source_online": "Booked online",
  "bookings.source_staff": "Booked by staff",
  "bookings.moved_from": "Moved",
  "bookings.with": "with {name}",

  // --- Public booking page ------------------------------------------------------------------
  "book.clinic_not_found": "We couldn't find this clinic.",
  "book.step_service": "Choose a service",
  "book.step_practitioner": "Choose a practitioner",
  "book.any_practitioner": "Any available practitioner",
  "book.step_time": "Choose a time",
  "book.step_details": "Your details",
  "book.no_services": "This clinic has no bookable services at the moment.",
  "book.no_slots_day": "No free times on this day.",
  "book.no_slots_week": "No free times this week.",
  "book.times_in": "Times are shown in {tz}.",
  "book.full_name": "Full name",
  "book.email": "Email",
  "book.phone": "Phone (optional)",
  "book.notes": "Anything we should know? (optional)",
  "book.notes_hint": "Please don't write sensitive health information here. You can tell your practitioner in person.",
  "book.privacy_notice":
    "{clinic} uses your details to manage this booking and to send you a confirmation and reminders. Dolphin Booking processes them on the clinic's behalf.",
  "book.privacy_link": "How your data is handled",
  "book.confirm_button": "Confirm booking",
  "book.summary": "Your booking",
  "book.change": "Change",
  "book.confirmed_title": "Booking confirmed",
  "book.confirmed_text": "We've emailed a confirmation to {email} with a link to view, move or cancel the booking.",
  "book.view_booking": "View your booking",
  "book.book_another": "Book another appointment",
  "book.powered_by": "Online booking by Dolphin Booking",
  "book.manage_title": "Your booking",
  "book.manage_not_found": "We couldn't find this booking. Check the link in your email.",
  "book.status_cancelled": "This booking has been cancelled.",
  "book.status_completed": "This appointment has taken place.",
  "book.status_no_show": "This appointment was recorded as missed.",
  "book.cancellable_until": "You can move or cancel online until {when}.",
  "book.too_late": "Changes are no longer possible online. Please contact the clinic.",
  "book.cancel_button": "Cancel booking",
  "book.cancel_confirm": "Are you sure you want to cancel this booking?",
  "book.cancel_reason": "Reason (optional)",
  "book.cancelled_done": "Your booking has been cancelled.",
  "book.move_button": "Move booking",
  "book.move_title": "Choose a new time",
  "book.moved_done": "Your booking has been moved.",
  "book.keep_time": "Keep the current time",
  "book.service": "Service",
  "book.practitioner": "Practitioner",
  "book.when": "When",
  "book.name": "Name",

  // --- API error codes -------------------------------------------------------------------------
  "errors.SLUG_TAKEN": "That booking page address is taken.",
  "errors.EMAIL_TAKEN": "An account with this email already exists. Sign in instead.",
  "errors.INVALID_OR_EXPIRED_TOKEN": "This link is invalid or has expired.",
  "errors.INVALID_CURRENT_PASSWORD": "The current password is incorrect.",
  "errors.PASSWORD_UNCHANGED": "Choose a password different from the current one.",
  "errors.PASSWORD_CHANGE_REQUIRED": "Change the initial password first.",
  "errors.SLOT_UNAVAILABLE": "That time is not available. Please choose another.",
  "errors.SLOT_TAKEN": "That time was just booked by someone else. Please choose another.",
  "errors.BOOKING_NOT_CANCELLABLE": "This booking can no longer be cancelled.",
  "errors.BOOKING_NOT_CHANGEABLE": "This booking can no longer be changed.",
  "errors.TOO_LATE_TO_CANCEL": "This booking can no longer be cancelled online. Please contact the clinic.",
  "errors.TOO_LATE_TO_CHANGE": "This booking can no longer be changed online. Please contact the clinic.",
  "errors.INVALID_STATUS_TRANSITION": "The booking's status cannot be changed that way.",
  "errors.PRACTITIONER_NOT_FOUND": "That practitioner does not exist or does not offer this service.",
  "errors.SERVICE_NOT_FOUND": "That service does not exist.",
  "errors.BOOKING_NOT_FOUND": "That booking does not exist.",
  "errors.CLINIC_NOT_FOUND": "That clinic does not exist.",
  "errors.UNAUTHENTICATED": "Please sign in to continue.",
  "errors.FORBIDDEN": "You do not have permission to do this.",
  "errors.RATE_LIMITED": "Too many attempts. Please wait a moment and try again.",
  "errors.NETWORK_ERROR": "Could not reach the server. Check your connection and try again.",
  "errors.INTERNAL_ERROR": "Something went wrong on our side. Please try again.",
  "errors.VALIDATION_ERROR": "Please check the highlighted fields.",
  "errors.BAD_ORIGIN": "This request was rejected for security reasons. Reload the page and try again.",

  // --- Team (CB-23) ----------------------------------------------------------------------
  "team.title": "Team",
  "team.subtitle": "Who can sign in to this clinic. Invite colleagues by email.",
  "team.members": "Members",
  "team.remove": "Remove",
  "team.remove_confirm": "Remove {name} from the clinic?",
  "team.invitations": "Open invitations",
  "team.invitations_empty": "No open invitations.",
  "team.invite_title": "Invite a colleague",
  "team.invite_email": "Email",
  "team.invite_role": "Role",
  "team.role_admin": "Administrator",
  "team.role_practitioner": "Practitioner",
  "team.role_owner": "Owner",
  "team.role_admin_hint": "Can manage services, practitioners, bookings and settings.",
  "team.role_practitioner_hint": "Can see services, practitioners and bookings.",
  "team.invite_button": "Send invitation",
  "team.invited": "Invitation sent.",
  "team.revoke": "Revoke",
  "team.expires": "Expires {date}",
  "invite.title": "Join {clinic}",
  "invite.text": "You have been invited to join {clinic} as {role}.",
  "invite.email": "Email",
  "invite.your_name": "Your name",
  "invite.password": "Password",
  "invite.password_existing_hint": "This email already has an account. Enter its password to join.",
  "invite.password_new_hint": "At least {n} characters. A short sentence works well.",
  "invite.accept": "Accept and sign in",
  "invite.not_open": "This invitation is no longer valid. Ask the clinic to send a new one.",
  "invite.not_found": "We couldn't find this invitation. Check the link in your email.",
  "clinics.new_title": "Create another clinic",
  "clinics.new_text": "You will be its owner. Your existing clinics are unaffected.",
  "clinics.new_button": "Create clinic",
  "clinics.new_link": "+ New clinic",

  // --- Public directory ----------------------------------------------------------------------
  "directory.title": "Find a clinic",
  "directory.subtitle": "Clinics that take online bookings.",
  "directory.search": "Search by name or address",
  "directory.empty": "No clinics match. Try another search.",
  "directory.book": "Book",
  "nav.directory": "Find a clinic",
  "settings.directory": "List this clinic in the public directory",
  "settings.directory_text":
    "Patients can find you by name or address. Your address and description are shown on the booking page.",
  "settings.address": "Address",
  "settings.phone": "Phone",
  "settings.description": "Description",
  "settings.sms": "Send SMS reminders as well as email",
  "settings.sms_text":
    "24 hours before the appointment, to patients who gave a phone number. Requires an SMS provider on the server.",

  // --- Insights (CB-25) ------------------------------------------------------------------------
  "nav.team": "Team",
  "nav.insights": "Insights",
  "insights.title": "Insights",
  "insights.subtitle": "Bookings, revenue and how full the calendar is.",
  "insights.range_week": "This week",
  "insights.range_month": "This month",
  "insights.range_30": "Last 30 days",
  "insights.range_90": "Last 90 days",
  "insights.bookings": "Bookings",
  "insights.completed": "Completed",
  "insights.cancelled": "Cancelled",
  "insights.no_show": "No-shows",
  "insights.revenue": "Revenue (completed)",
  "insights.expected": "Expected (incl. upcoming)",
  "insights.online_share": "Booked online",
  "insights.by_day": "Bookings per day",
  "insights.by_practitioner": "Practitioner utilization",
  "insights.by_practitioner_hint": "Booked minutes as a share of scheduled working hours in the period.",
  "insights.booked": "Booked",
  "insights.scheduled": "Scheduled",
  "insights.utilization": "Utilization",
  "insights.no_hours": "No hours",
  "insights.by_service": "Services",
  "insights.empty": "No bookings in this period yet.",

  // --- Calendar feed (CB-27) -----------------------------------------------------------------------
  "calendar.title": "Calendar subscription",
  "calendar.text":
    "A private link that Google Calendar, Outlook or Apple Calendar can subscribe to. It shows this practitioner's appointments and updates automatically.",
  "calendar.issue": "Create link",
  "calendar.reissue": "Create a new link",
  "calendar.revoke": "Remove link",
  "calendar.issued": "Copy this link now; it is only shown once. Creating a new link disables the old one.",
  "calendar.active": "A subscription link exists.",
  "errors.ALREADY_MEMBER": "That person is already a member of this clinic.",
  "errors.LAST_OWNER": "A clinic must keep at least one owner.",
  "errors.INVITATION_NOT_OPEN": "This invitation is no longer valid.",
  "errors.INVITATION_NOT_FOUND": "This invitation does not exist.",
  "errors.MEMBER_NOT_FOUND": "That person is not a member of this clinic.",

  // --- Subscription state (dashboard banner, settings) ------------------------
  "sub.state_trialing": "Trial",
  "sub.state_active": "Active",
  "sub.state_past_due": "Past due",
  "sub.state_expired": "Expired",
  "sub.state_cancelled": "Cancelled",
  "sub.state_suspended": "Suspended",
  "sub.state_none": "No subscription",
  "sub.banner_trialing": "Free trial: {n} days left. Contact the platform to continue after the trial.",
  "sub.banner_trial_last_day": "Your free trial ends today. Contact the platform to continue.",
  "sub.banner_past_due":
    "Your subscription payment is overdue. Online booking closes in a few days unless it is settled.",
  "sub.banner_expired":
    "Your subscription has expired. Your booking page is closed to new bookings until it is renewed.",
  "sub.banner_cancelled": "Your subscription is cancelled. Your booking page is closed to new bookings.",
  "sub.banner_suspended": "This clinic has been suspended by the platform. {reason}",
  "settings.subscription": "Subscription",
  "settings.sub_text": "Subscriptions are managed by the platform. Contact us to change your plan.",
  "settings.sub_plan": "Plan",
  "settings.sub_status": "Status",
  "settings.sub_price": "Price",
  "settings.sub_trial_ends": "Trial ends",
  "settings.sub_paid_until": "Paid until",
  "settings.sub_practitioners": "Practitioners included",
  "settings.sub_unlimited": "Unlimited",
  "sub.per_month": "per month",
  "sub.per_year": "per year",
  "book.closed_title": "Online booking is paused",
  "book.closed_text": "This clinic is not taking online bookings right now. Please contact the clinic directly.",

  // --- Platform admin area ------------------------------------------------------
  "admin.title": "Platform admin",
  "admin.login.title": "Sign in to the admin console",
  "admin.login.subtitle": "For platform administrators only.",
  "admin.nav.password": "Change password",
  "admin.password.title": "Change password",
  "admin.password.first_time":
    "This is your first sign-in. Replace the initial password from the server configuration with your own before you continue.",
  "admin.password.text": "You'll stay signed in here; your other sessions are signed out.",
  "admin.nav.overview": "Overview",
  "admin.nav.clinics": "Clinics",
  "admin.nav.users": "Users",
  "admin.nav.plans": "Plans",
  "admin.nav.audit": "Audit log",
  "admin.nav.settings": "Settings",
  "admin.ip_blocked":
    "Your address {ip} is not on the admin allowlist. Add it to ADMIN_IP_ALLOWLIST on the server, or ask another admin to add it under Settings.",
  "admin.overview.title": "Overview",
  "admin.overview.clinics": "Clinics",
  "admin.overview.active": "active",
  "admin.overview.suspended": "suspended",
  "admin.overview.new_30": "New in 30 days",
  "admin.overview.users": "Users",
  "admin.overview.bookings_30": "Bookings in 30 days",
  "admin.overview.bookings_total": "Bookings total",
  "admin.overview.emails_30": "Emails sent in 30 days",
  "admin.overview.sms_30": "SMS sent in 30 days",
  "admin.overview.by_state": "Clinics by subscription state",
  "admin.overview.recent": "Latest sign-ups",
  "admin.overview.unavailable": "n/a",
  "admin.clinics.title": "Clinics",
  "admin.clinics.subtitle": "Every clinic on the platform, with its owner and subscription.",
  "admin.clinics.search": "Search by name, address or owner email",
  "admin.clinics.filter_state": "State",
  "admin.clinics.all": "All",
  "admin.clinics.empty": "No clinics match.",
  "admin.clinics.owner": "Owner",
  "admin.clinics.plan": "Plan",
  "admin.clinics.created": "Created",
  "admin.clinics.members": "Members",
  "admin.clinics.practitioners": "Practitioners",
  "admin.clinics.bookings_30": "Bookings (30 d)",
  "admin.clinics.open": "Open",
  "admin.clinics.total": "{n} clinics",
  "admin.clinic.details": "Details",
  "admin.clinic.timezone": "Time zone",
  "admin.clinic.address": "Address",
  "admin.clinic.phone": "Phone",
  "admin.clinic.bookings_total": "Bookings total",
  "admin.clinic.services": "Active services",
  "admin.clinic.listed": "In directory",
  "admin.clinic.sms": "SMS reminders",
  "admin.clinic.booking_page": "Booking page",
  "admin.clinic.members": "Members",
  "admin.clinic.suspend": "Suspend clinic",
  "admin.clinic.suspend_reason": "Reason (shown to the clinic's staff)",
  "admin.clinic.suspend_confirm": "Suspend {name}? Its booking page closes immediately.",
  "admin.clinic.unsuspend": "Lift suspension",
  "admin.clinic.suspended_since": "Suspended since {date}",
  "admin.clinic.audit": "Admin actions on this clinic",
  "admin.sub.title": "Subscription",
  "admin.sub.plan": "Plan",
  "admin.sub.currency": "Currency",
  "admin.sub.interval": "Billing period",
  "admin.sub.interval_month": "Monthly",
  "admin.sub.interval_year": "Yearly",
  "admin.sub.status": "Status",
  "admin.sub.status_trialing": "Trial",
  "admin.sub.status_active": "Active",
  "admin.sub.status_past_due": "Past due",
  "admin.sub.status_cancelled": "Cancelled",
  "admin.sub.trial_ends": "Trial ends",
  "admin.sub.period_end": "Paid until",
  "admin.sub.period_end_hint": "Leave empty for open-ended (manual invoicing).",
  "admin.sub.grace": "After the paid-until date there is a {n}-day grace period before online booking closes.",
  "admin.sub.external_ref": "External reference",
  "admin.sub.notes": "Notes",
  "admin.sub.save": "Save subscription",
  "admin.sub.saved": "Subscription saved.",
  "admin.sub.price": "Price",
  "admin.sub.no_price": "No price for this combination",
  "admin.users.title": "Users",
  "admin.users.subtitle": "Everyone with a staff account, across all clinics.",
  "admin.users.search": "Search by email or name",
  "admin.users.empty": "No users match.",
  "admin.users.no_clinics": "No clinics",
  "admin.users.joined": "Joined {date}",
  "admin.plans.title": "Plans",
  "admin.plans.subtitle": "What clinics pay. New clinics start on the default plan with its trial.",
  "admin.plans.new": "New plan",
  "admin.plans.edit": "Edit",
  "admin.plans.code": "Code",
  "admin.plans.code_hint": "Short identifier, e.g. standard. Lowercase letters, digits and dashes.",
  "admin.plans.name": "Name",
  "admin.plans.description": "Description",
  "admin.plans.trial_days": "Trial days",
  "admin.plans.max_practitioners": "Max practitioners",
  "admin.plans.max_practitioners_hint": "Leave empty for unlimited.",
  "admin.plans.is_default": "Default plan for new sign-ups",
  "admin.plans.active": "Available for new subscriptions",
  "admin.plans.sort_order": "Sort order",
  "admin.plans.prices": "Prices",
  "admin.plans.prices_hint": "Amounts in whole units (e.g. 299). Leave a field empty to not offer that combination.",
  "admin.plans.monthly": "Monthly",
  "admin.plans.yearly": "Yearly",
  "admin.plans.clinics": "{n} clinics",
  "admin.plans.save": "Save plan",
  "admin.plans.saved": "Plan saved.",
  "admin.plans.default": "Default",
  "admin.plans.inactive": "Inactive",
  "admin.plans.unlimited": "Unlimited practitioners",
  "admin.plans.max_n": "Up to {n} practitioners",
  "admin.plans.trial_n": "{n}-day trial",
  "admin.plans.cancel_edit": "Cancel",
  "admin.audit.title": "Audit log",
  "admin.audit.subtitle": "Every change and sign-in in the admin console.",
  "admin.audit.empty": "Nothing yet.",
  "admin.audit.when": "When",
  "admin.audit.who": "Who",
  "admin.audit.action": "Action",
  "admin.audit.target": "Target",
  "admin.audit.details": "Details",
  "admin.settings.title": "Settings",
  "admin.settings.ip_title": "Admin IP allowlist",
  "admin.settings.ip_text":
    "The admin area only opens from these addresses. Addresses from the server configuration (ADMIN_IP_ALLOWLIST) always apply; the ones you add here are stored in the database and can be changed without a deployment.",
  "admin.settings.ip_configured": "From server configuration",
  "admin.settings.ip_extra": "Additional addresses (one per line)",
  "admin.settings.ip_hint": "IPv4/IPv6 addresses or CIDR ranges, e.g. 203.0.113.4 or 10.0.0.0/8.",
  "admin.settings.your_ip": "Your current address: {ip}",
  "admin.settings.add_mine": "Add my address",
  "admin.settings.save": "Save allowlist",
  "admin.settings.saved": "Allowlist saved.",
  "common.search": "Search",
  "common.load_more": "Load more",
  "common.none": "None",
  "errors.CLINIC_NOT_ACCEPTING_BOOKINGS": "This clinic is not taking online bookings at the moment.",
  "errors.ADMIN_IP_NOT_ALLOWED": "Your address is not on the admin allowlist.",
  "errors.PLAN_LIMIT_PRACTITIONERS": "Your plan does not allow more practitioners. Contact the platform to upgrade.",
  "errors.PLAN_CODE_TAKEN": "A plan with this code already exists.",
  "errors.DEFAULT_PLAN_REQUIRED": "Make another plan the default first.",
  "errors.DEFAULT_PLAN_INACTIVE": "The default plan must be available.",
  "errors.PRICE_NOT_DEFINED": "This plan has no price for that currency and billing period.",
  "errors.TRIAL_END_REQUIRED": "A trial needs an end date.",
  "errors.WOULD_LOCK_YOU_OUT": "That would remove your own address from the allowlist.",
  "errors.CANNOT_CHANGE_SELF": "You cannot change your own admin access.",
  "errors.ALREADY_SUSPENDED": "This clinic is already suspended.",
  "errors.NOT_SUSPENDED": "This clinic is not suspended.",
  "errors.PLAN_NOT_FOUND": "This plan does not exist.",
  "errors.USER_NOT_FOUND": "This user does not exist.",
  "errors.DUPLICATE_PRICE": "The same currency and period is listed twice.",

  // --- Site footer and legal pages ------------------------------------------------
  "footer.product": "Product",
  "footer.legal": "Legal",
  "footer.privacy": "Privacy policy",
  "footer.terms": "Terms of service",
  "footer.dpa": "Data processing agreement",
  "footer.cookies": "Cookie policy",
  "footer.cvr": "CVR {cvr}",
  "footer.no_tracking": "No tracking, no ad cookies.",
  "legal.other_docs": "Other documents",
} as const;
